/**
 * Onboarding welcome modal — fires once on first sign-in to introduce
 * the drill → coach → leak-report loop. 3 paginated screens inside a
 * single centered modal. Standard SaaS onboarding pattern.
 *
 * Trigger: `billing_status.onboarding_welcome_shown === false`.
 *
 * Mounted via `<WelcomeModalProvider>` in App.tsx — sibling of
 * `<FirstLeakReportModalProvider>`. Auto-fires by watching the
 * `["billing-status"]` TanStack query. Welcome takes priority over
 * first-leak-report; the first-leak modal guards on
 * `onboardingWelcomeShown` being true before it fires.
 *
 * Dismiss flow (any of):
 *   1. Final CTA "Start drilling" → POST acknowledge, modal closes,
 *      user stays on /drills (the page behind the modal — they're
 *      already in the right place).
 *   2. X button → same.
 *   3. Backdrop click → same.
 *
 * Once the flag flips to True on the backend, the next /billing/status
 * read returns `onboarding_welcome_shown: True` and the modal never
 * re-fires for the user.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  acknowledgeOnboardingWelcome,
  getBillingStatus,
  shouldShowWelcomeModal,
} from "@/features/billing/lib/billing";
import { useAuth } from "@/features/auth/lib/authContext";
import { ICON_STROKE, X } from "@/shared/components/icons";

export function WelcomeModalProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const signedIn = authStatus.kind === "signedIn";

  const { data: status } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
    enabled: signedIn,
  });
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Ref guard prevents multi-open if the query refetches mid-display.
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (!signedIn) return;
    if (!status) return;
    if (shouldShowWelcomeModal(status) && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setOpen(true);
    }
  }, [signedIn, status]);

  const acknowledge = async () => {
    setOpen(false);
    try {
      await acknowledgeOnboardingWelcome();
    } catch {
      // Silent failure — modal re-fires next session, mild annoyance not a bug.
    }
    queryClient.invalidateQueries({ queryKey: ["billing-status"] });
  };

  return (
    <>
      {children}
      {open && <WelcomeModalView onClose={acknowledge} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// The modal itself — 3 paginated screens
// ---------------------------------------------------------------------------

const TOTAL_SCREENS = 2;

function WelcomeModalView({ onClose }: { onClose: () => void }) {
  const [screen, setScreen] = useState(1);

  const next = () => {
    if (screen < TOTAL_SCREENS) setScreen(screen + 1);
    else onClose();
  };
  const back = () => {
    if (screen > 1) setScreen(screen - 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-2xl border border-accent/40 bg-surface relative shadow-[0_28px_60px_-20px_rgba(122,136,200,0.7)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 pt-8 space-y-5">
          <Reticle />
          {screen === 1 && <ScreenWelcome />}
          {screen === 2 && <ScreenFree />}
        </div>

        <div className="px-6 pt-2 pb-5 border-t border-border/40 space-y-3">
          <Dots current={screen} total={TOTAL_SCREENS} />
          <div className="flex gap-2">
            {screen > 1 && (
              <button
                type="button"
                onClick={back}
                className="flex-1 py-3 rounded-xl border border-border text-text-muted text-sm font-medium hover:border-text-muted/40 hover:text-text transition"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="flex-1 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(122,136,200,0.6)]"
            >
              {screen === TOTAL_SCREENS ? "Start drilling" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Reticle() {
  return (
    <div className="mx-auto w-12 h-12 flex items-center justify-center">
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#7A88C8"
        strokeWidth="1.5"
        strokeLinecap="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="3.75" />
        <line x1="12" y1="1.5" x2="12" y2="5" />
        <line x1="12" y1="19" x2="12" y2="22.5" />
        <line x1="1.5" y1="12" x2="5" y2="12" />
        <line x1="19" y1="12" x2="22.5" y2="12" />
        <circle cx="12" cy="12" r="1" fill="#7A88C8" stroke="none" />
      </svg>
    </div>
  );
}

function ScreenWelcome() {
  return (
    <div className="text-center space-y-3">
      <h2 className="text-2xl font-semibold text-text tracking-tight">
        Welcome to Holdem Drills
      </h2>
      <p className="text-sm text-text-muted leading-relaxed">
        You drill real spots — one decision at a time — and the coach reads
        every one. After 25 reads it delivers your first leak report: the
        patterns quietly costing you the most EV.
      </p>
      <p className="text-sm text-text-muted leading-relaxed">
        Start with <span className="text-brand font-medium">Fundamentals</span>
        {" "}(the orange card) — variety reps that let the coach calibrate its
        first read on you.
      </p>
    </div>
  );
}

function ScreenFree() {
  return (
    <div className="text-center space-y-3">
      <h2 className="text-2xl font-semibold text-text tracking-tight">
        Drill freely
      </h2>
      <p className="text-sm text-text-muted leading-relaxed">
        You're on the free tier — drill as much as you want, all the way to
        your first leak report. After that, keep drilling with the coach on a
        subscription. Your quizzes, study sheets, and saved report stay free,
        always.
      </p>
      <p className="text-xs text-text-muted/70">
        No trial. No card on file.
      </p>
    </div>
  );
}

function Dots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex justify-center gap-1.5" aria-label={`Step ${current} of ${total}`}>
      {Array.from({ length: total }).map((_, i) => {
        const active = i + 1 === current;
        return (
          <span
            key={i}
            className={[
              "w-1.5 h-1.5 rounded-full transition",
              active ? "bg-accent" : "bg-text/20",
            ].join(" ")}
            aria-hidden
          />
        );
      })}
    </div>
  );
}
